import { Auswahl } from "../Auswahl";
import { Farbmenuselect } from "./Farbmenuselect";
import {
  getRichProfilkategorien,
  applyProfileChangeToElements,
} from "../../arrangement/elementFunctions";
import { PositionInput } from "./ZaunCircleMenuItemProfil";
import * as Dialogs from "oc-ui/dialogs/Dialogs";
import { SvgButton } from "../../commons/SvgButton";
import { TrashBinSvg } from "../../inlinesvgs/TrashBinSvg";
import { MenuItemSmallButton } from "./MenuItemSmallButton";
import React from "react";

export function DekorOptionen() {
  const agt = APP.arrangement;
  const { Profilkategorie, Profil, Profilleiste } = agt.auswahlAccessoren;
  const alleProfilkategorien = getRichProfilkategorien();
  const profilkategorien = alleProfilkategorien.filter(
    (k) => k.kategorieAuswahlwert.Typinfo !== "Lichtleiste"
  );
  const isLichtleisteMöglich = alleProfilkategorien.some(
    (k) => k.kategorieAuswahlwert.Typinfo === "Lichtleiste"
  );

  const elemente = agt.getSelectedOrAllElemente();
  const sindElementeSelektiert = agt.selectedObjects.some((e) => e.isElement);
  const elementeMitProfil = elemente.filter((e) => e.showProfil);
  const hatProfilAktiv = elementeMitProfil.length > 0;
  const hatDekorprofilAktiv = elementeMitProfil.some((e) => e.hasDekorprofil);
  const erstesElementMitProfil = elementeMitProfil[0];
  const profilKategorieAuswahlwert = erstesElementMitProfil?.profilKategorieAuswahlwert;

  const isRhombusOderRhombusHolz = elemente.some((e) => e.isRhombusOderRhombusHolz);
  const hatBasisprofile =
    isRhombusOderRhombusHolz && !(erstesElementMitProfil?.sonderprofilMinMax?.mindesthöhe > 0);

  const handleProfileChange = (
    kategorieAuswahlwert,
    updates: { profil?: any; profilleiste?: any }
  ) => {
    applyProfileChangeToElements(elemente, kategorieAuswahlwert, updates, sindElementeSelektiert);
  };
  const removeProfile = () => {
    elemente.forEach((e) => {
      e.showProfil = null;
      e.profilKategorieAuswahlwert = null;
      e.profilAuswahlwert = null;
      e.profilPosition = null;
    });
    agt.update3d();
    APP.dirty();
    APP.logUserAction({ action: "removeDekorprofileButton" });
  };

  const addLichtleisteUndÖffneOptionen = async () => {
    elemente.forEach((e) => {
      e.setShowProfil(true);
      e.addLichtleiste();
    });
    APP.arrangement.activeTabKey = "Lichtoptionen";
    APP.dirty();
    APP.logUserAction({ action: "addLichtleisteButton" });
    await Commons.sleep(50);
    APP.trigger("maybeShowLichtleistenOptionen");
  };

  // Globales min/max über alle Elemente mit Profil bestimmen (kleinstes min, größtes max).
  // sonderprofilMinMax.minPosition berücksichtigt bereits profilPositionDelta und offsetY bei Toren,
  // d.h. Positionen hinter dem Rahmen oder mit zugeschnittenem Profil sind ausgeschlossen.
  const globalMinPosition = hatProfilAktiv
    ? Math.min(...elementeMitProfil.map((e) => e.sonderprofilMinMax.minPosition))
    : undefined;
  const globalMaxPosition = hatProfilAktiv
    ? Math.max(...elementeMitProfil.map((e) => e.sonderprofilMinMax.maxPosition))
    : undefined;

  return (
    <div
      key="dekor-tab"
      className="options-tab"
      role="region"
      aria-label={I18N("aria::Dekoroptionen")}
    >
      {hatProfilAktiv && (
        <div className="flex-column" role="group" aria-label={I18N("aria::Profiloptionen")}>
          <div className="flex-row gap-2">
            {(!isRhombusOderRhombusHolz || hatBasisprofile) && (
              <>
                {I18N("Profil Position")}
                <PositionInput
                  key={erstesElementMitProfil.id + ".y"}
                  element={erstesElementMitProfil}
                  overrideMinPosition={!sindElementeSelektiert ? globalMinPosition : undefined}
                  overrideMaxPosition={!sindElementeSelektiert ? globalMaxPosition : undefined}
                  value={
                    elementeMitProfil.every(
                      (e) => e.profilPosition === erstesElementMitProfil.profilPosition
                    )
                      ? erstesElementMitProfil.profilPosition ??
                        erstesElementMitProfil.sonderprofilMinMax.maxPosition
                      : null
                  }
                  setter={(value) => {
                    const hatGemischtePositionen = !elementeMitProfil.every(
                      (e) => e.profilPosition === erstesElementMitProfil.profilPosition
                    );
                    agt.elementeMitProfilpositionsWarnung.clear();
                    elementeMitProfil.forEach((e) => {
                      if (
                        sindElementeSelektiert ||
                        !e.profilPositionIndividualisiert ||
                        hatGemischtePositionen
                      ) {
                        if (typeof value === "number") {
                          e.profilPosition = value;
                        }
                      }
                      if (sindElementeSelektiert) {
                        e.profilPositionIndividualisiert = true;
                        e.showIndividualisiert = true;
                      }
                      if (!sindElementeSelektiert && hatGemischtePositionen) {
                        e.profilPositionIndividualisiert = false;
                      }
                    });
                    agt.profilPosition_ = value as number | "center" | null;

                    // Warnung: Prüfe ob die Position bei einzelnen Elementen außerhalb deren min/max liegt.
                    if (typeof value === "number" && !sindElementeSelektiert) {
                      const betroffeneElemente = elementeMitProfil.filter((e) => {
                        const { minPosition, maxPosition } = e.sonderprofilMinMax;
                        return value < minPosition + 1 || value > maxPosition;
                      });
                      if (betroffeneElemente.length > 0) {
                        betroffeneElemente.forEach((e) => {
                          agt.elementeMitProfilpositionsWarnung.add(e);
                        });
                        Dialogs.showToast(
                          I18N(
                            "Die gewählte Profilposition wird auf den nächsten gültigen Wert angepasst."
                          ),
                          {
                            type: "warning",
                            delay: 6000,
                            onClose: () => {
                              betroffeneElemente.forEach((e) => {
                                agt.elementeMitProfilpositionsWarnung.delete(e);
                              });
                              APP.dirty();
                            },
                          }
                        );
                      }
                    }

                    APP.dirty();
                  }}
                />
              </>
            )}
            <SvgButton title={I18N("Profil entfernen")} onClick={removeProfile} size="large">
              <TrashBinSvg />
            </SvgButton>
          </div>
          {hatDekorprofilAktiv && isLichtleisteMöglich && (
            <MenuItemSmallButton
              className="lichtleiste-ersetzen-button"
              onClick={async () => {
                removeProfile();
                await addLichtleisteUndÖffneOptionen();
              }}
            >
              {I18N("durch Lichtleiste ersetzen")}
            </MenuItemSmallButton>
          )}
        </div>
      )}

      {!hatProfilAktiv && isLichtleisteMöglich && (
        <MenuItemSmallButton
          className="lichtleiste-einfuegen-button"
          onClick={async () => {
            await addLichtleisteUndÖffneOptionen();
          }}
        >
          {I18N("Lichtleiste einfügen")}
        </MenuItemSmallButton>
      )}

      {profilkategorien.map(
        ({ kategorieAuswahlwert, profilAuswahlwerte, profilleisteAuswahlwerte }, i) => (
          <fieldset className="DekorOptionen" key={i}>
            <legend className="headline-kategorie">{kategorieAuswahlwert.Name}</legend>
            <div className="flex column-container" data-group-key="Profilkategorie">
              {profilleisteAuswahlwerte.length > 1 &&
                kategorieAuswahlwert.Typinfo !== "Lichtleiste" && (
                  <Auswahl
                    Key="Profilleiste"
                    auswahl={Profilleiste.auswahl}
                    small={true}
                    showHeadline={false}
                    auswahlwerte={
                      profilleisteAuswahlwerte ??
                      (agt.profilleisteAuswahlwert != null ? [agt.profilleisteAuswahlwert] : [])
                    }
                    value={Profilleiste.auswahlwert}
                    onChange={(val) => {
                      handleProfileChange(kategorieAuswahlwert, { profilleiste: val });
                    }}
                  />
                )}

              <Farbmenuselect
                auswahlwerte={profilAuswahlwerte}
                value={
                  profilKategorieAuswahlwert == kategorieAuswahlwert &&
                  (Profil.auswahlwert ?? agt.profilAuswahlwert)
                }
                onChange={(profil) => {
                  handleProfileChange(kategorieAuswahlwert, { profil });
                }}
                className="pfosten-item"
              />
            </div>
          </fieldset>
        )
      )}
    </div>
  );
}
